import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Trophy, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Calendar,
  ChevronRight,
  Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Flag options for tournaments
const FLAG_OPTIONS = [
  { value: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', label: 'England' },
  { value: '🇪🇸', label: 'Spain' },
  { value: '🇮🇹', label: 'Italy' },
  { value: '🇩🇪', label: 'Germany' },
  { value: '🇫🇷', label: 'France' },
  { value: '🇳🇱', label: 'Netherlands' },
  { value: '🇵🇹', label: 'Portugal' },
  { value: '🇧🇪', label: 'Belgium' },
  { value: '🇪🇺', label: 'Europe' },
  { value: '🇬🇲', label: 'The Gambia' },
  { value: '🇸🇳', label: 'Senegal' },
  { value: '🇳🇬', label: 'Nigeria' },
  { value: '🌍', label: 'Africa' },
  { value: '🌎', label: 'Americas' },
  { value: '🌏', label: 'Asia' },
  { value: '🏆', label: 'International' },
];

export default function TournamentsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'football',
    country: '',
    flag: '🏆',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tournaments?active_only=false`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTournament(null);
    setFormData({
      name: '',
      category: 'football',
      country: '',
      flag: '🏆',
      description: '',
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (tournament) => {
    setEditingTournament(tournament);
    setFormData({
      name: tournament.name,
      category: tournament.category,
      country: tournament.country || '',
      flag: tournament.flag || '🏆',
      description: tournament.description || '',
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Tournament name is required');
      return;
    }

    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };

      if (editingTournament) {
        // Update
        await axios.put(
          `${API_URL}/api/tournaments/${editingTournament.id}`,
          formData,
          { headers }
        );
        toast.success('Tournament updated successfully');
      } else {
        // Create
        await axios.post(`${API_URL}/api/tournaments`, formData, { headers });
        toast.success('Tournament created successfully');
      }

      setIsDialogOpen(false);
      fetchTournaments();
    } catch (error) {
      console.error('Failed to save tournament:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tournament');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tournament) => {
    if (!window.confirm(`Are you sure you want to delete "${tournament.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/tournaments/${tournament.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Tournament deleted');
      fetchTournaments();
    } catch (error) {
      console.error('Failed to delete tournament:', error);
      toast.error('Failed to delete tournament');
    }
  };

  const toggleActive = async (tournament) => {
    try {
      await axios.put(
        `${API_URL}/api/tournaments/${tournament.id}`,
        { is_active: !tournament.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Tournament ${tournament.is_active ? 'deactivated' : 'activated'}`);
      fetchTournaments();
    } catch (error) {
      toast.error('Failed to update tournament');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trophy className="w-7 h-7 text-[#0055FF]" />
              Tournaments
            </h1>
            <p className="text-gray-500 mt-1">Manage football tournaments and competitions</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#0055FF] hover:bg-[#0044CC]">
            <Plus className="w-4 h-4 mr-2" />
            Add Tournament
          </Button>
        </div>

        {/* Tournaments Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
          </div>
        ) : tournaments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tournaments.map((tournament) => (
              <Card key={tournament.id} className={`overflow-hidden transition-all ${!tournament.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-0">
                  <div className="p-4 border-b bg-gradient-to-r from-[#0055FF]/5 to-purple-500/5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{tournament.flag || '🏆'}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{tournament.name}</h3>
                          <p className="text-sm text-gray-500">{tournament.country}</p>
                        </div>
                      </div>
                      <Badge variant={tournament.is_active ? 'default' : 'secondary'}>
                        {tournament.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {tournament.fixtures_count || 0} fixtures
                      </span>
                      <Badge variant="outline" className="capitalize">{tournament.category}</Badge>
                    </div>

                    {tournament.description && (
                      <p className="text-sm text-gray-500 mb-4 line-clamp-2">{tournament.description}</p>
                    )}

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/fixtures?tournament=${tournament.id}`)}
                        className="flex-1"
                      >
                        View Fixtures
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(tournament)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => toggleActive(tournament)}
                        className={tournament.is_active ? 'text-orange-500' : 'text-green-500'}
                      >
                        {tournament.is_active ? '⏸' : '▶'}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(tournament)} className="text-red-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tournaments Yet</h3>
              <p className="text-gray-500 mb-6">Create your first tournament to get started</p>
              <Button onClick={openCreateDialog} className="bg-[#0055FF]">
                <Plus className="w-4 h-4 mr-2" />
                Add Tournament
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingTournament ? 'Edit Tournament' : 'Create Tournament'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
                <Input
                  placeholder="e.g., Premier League"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="football">Football</SelectItem>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
                <Input
                  placeholder="e.g., England"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Flag/Icon</label>
                <Select
                  value={formData.flag}
                  onValueChange={(value) => setFormData({ ...formData, flag: value })}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">{formData.flag}</span>
                        <span>{FLAG_OPTIONS.find(f => f.value === formData.flag)?.label || 'Select flag'}</span>
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {FLAG_OPTIONS.map((flag) => (
                      <SelectItem key={flag.value} value={flag.value}>
                        <span className="flex items-center gap-2">
                          <span className="text-xl">{flag.value}</span>
                          <span>{flag.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <Input
                  placeholder="Brief description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0055FF]">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTournament ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
